import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  ChartBar,
  Storefront,
  FileText,
  Bank,
  UserCircle,
  SignOut,
  Code,
  Lightning,
  ShoppingCart,
} from "@phosphor-icons/react";
import NotificationBell from "@/components/NotificationBell";

function buildNav(products) {
  const items = [
    { to: "/publisher", label: "Dashboard", icon: ChartBar, end: true },
    { to: "/publisher/brands", label: "Brands", icon: Storefront },
    { to: "/publisher/reports", label: "Reports", icon: FileText },
  ];
  // Yieldkit only visible when admin has bound an account to this pub.
  // Currently CPA-only — YK has said CPC will follow later, at which
  // point we may want to split the surface (or just keep one and badge
  // each row's offer_type).
  if ((products || []).includes("cpc")) {
    items.push({ to: "/publisher/cpc", label: "Yieldkit", icon: Lightning });
  }
  // Shopnomix Amazon EPC — admin assigns one of the 3 feeds.
  if ((products || []).includes("shopnomix")) {
    items.push({ to: "/publisher/amazon", label: "Amazon (EPC)", icon: ShoppingCart });
  }
  items.push(
    { to: "/publisher/integrations", label: "API & Integrations", icon: Code },
    { to: "/publisher/bank-details", label: "Bank Details", icon: Bank },
    { to: "/publisher/profile", label: "Profile", icon: UserCircle },
  );
  return items;
}

function initials(user) {
  const f = (user?.first_name || "").trim();
  const l = (user?.last_name || "").trim();
  if (f || l) return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase();
  return (user?.email || "?").charAt(0).toUpperCase();
}

export default function PubLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const navItems = buildNav(user?.products_enabled);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-white flex" data-testid="publisher-layout">
      <aside className="w-60 border-r border-slate-200 bg-slate-50 flex flex-col shrink-0">
        {/* Brand */}
        <div className="p-5 border-b border-slate-200">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-[#002FA7] text-white flex items-center justify-center font-heading font-black text-base tracking-tight rounded-sm">
              E
            </div>
            <div>
              <h1 className="font-heading text-[15px] font-bold text-slate-900 leading-none tracking-tight">
                Engagino
              </h1>
              <p className="text-[10px] text-slate-500 mt-1 tracking-[0.2em] uppercase font-bold">
                Publisher
              </p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-0.5">
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 px-3 pb-2 pt-1">
            Workspace
          </p>
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-[#002FA7] text-white"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
              data-testid={`pub-nav-${item.label.toLowerCase().replace(" ", "-")}`}
            >
              <item.icon size={18} weight={item.end ? "bold" : "regular"} />
              {item.label}
            </NavLink>
          ))}
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 w-full transition-all duration-200"
            data-testid="pub-logout-btn"
          >
            <SignOut size={18} />
            Logout
          </button>
        </nav>

        {/* User badge — kept at bottom for identity context */}
        <div className="p-3 border-t border-slate-200">
          <div className="flex items-center gap-3 px-2 py-2">
            <div
              className="w-9 h-9 rounded-full bg-[#002FA7] text-white flex items-center justify-center font-heading font-bold text-xs tracking-tight shrink-0"
              data-testid="pub-avatar"
            >
              {initials(user)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold text-slate-900 truncate">
                {user?.company_name || `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "Publisher"}
              </div>
              <div className="text-[10px] font-mono text-slate-500 truncate">
                SubID {user?.sub_id || "—"}
              </div>
            </div>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <div className="sticky top-0 z-30 flex items-center justify-end gap-2 px-6 py-2.5 bg-white/80 backdrop-blur-sm border-b border-slate-100">
          <NotificationBell />
        </div>
        <Outlet />
      </main>
    </div>
  );
}
